import { captureException, withScope } from "@sentry/browser";
import type Quill from "quill";
import Delta from "quill-delta";
import { clamp } from "@/shared/lib/clamp";
import { DEFAULT_TEXT_COLOR } from "../constantsV2";
import type { Bounds, StyleSummary, TextAlign } from "../typesV2";

const NON_LEFT_TEXT_ALIGNS: readonly TextAlign[] = ["center", "right"];
const QUILL_BOUNDS_REPORT_INTERVAL_MS = 5 * 60 * 1000;
let lastQuillBoundsRangeErrorReportAt = 0;

export function placeCaretAtEnd(el: HTMLElement) {
    el.focus();

    const selection = window.getSelection();
    if (!selection) {
        return;
    }

    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);

    selection.removeAllRanges();
    selection.addRange(range);
}

export function isUIElement(el: Element | null): boolean {
    if (!el) return false;

    return Boolean(el.closest("[data-ui-root]"));
}

export function sceneToClient(pt: { x: number; y: number }, m: number[], dpr: number) {
    const deviceX = m[0] * pt.x + m[2] * pt.y + m[4];
    const deviceY = m[1] * pt.x + m[3] * pt.y + m[5];

    const clientX = deviceX / dpr;
    const clientY = deviceY / dpr;

    return {
        clientX,
        clientY,
    };
}

// Добавляет align в delta, чтобы при копировании/вставке текста на доске сохранялось корректное выравнивание.
export function buildDeltaWithTextAlign(
    sourceDelta: Delta | { ops?: Delta["ops"] },
    textAlign: TextAlign,
): Delta {
    if (!NON_LEFT_TEXT_ALIGNS.includes(textAlign)) {
        return new Delta(sourceDelta.ops ?? []);
    }

    const sourceOps = sourceDelta.ops ?? [];
    if (sourceOps.length === 0) {
        return new Delta(sourceOps);
    }

    const normalizedOps = sourceOps.map((op) => {
        if (typeof op.insert !== "string" || !op.insert.includes("\n")) {
            return op;
        }

        return {
            ...op,
            attributes: {
                ...(op.attributes ?? {}),
                align: textAlign,
            },
        };
    });

    return new Delta(normalizedOps);
}

function countNewlines(line: string) {
    let count = 0;
    for (let i = 0; i < line.length; i++)
        if (line.charCodeAt(i) === 10) {
            count++;
        }
    return count;
}

export function summarizeDelta(
    delta: Delta,
    opts?: { defaultColor?: string; topNColors?: number },
): StyleSummary {
    const defaultColor = opts?.defaultColor ?? "#2d2d2d";

    const colors = new Set<string>();
    const links = new Set<string>();
    const boldValues = new Set<boolean>();
    const strikeValues = new Set<boolean>();
    const italicValues = new Set<boolean>();
    const underlineValues = new Set<boolean>();
    let hasBulletList = false;

    const ops = delta.ops;
    if (!ops?.length) {
        return {
            firstColor: defaultColor,
            secondColor: defaultColor,
            link: "",
            hasBulletList: false,
            isBold: false,
            isStriked: false,
            isItalic: false,
            isUnderline: false,
        };
    }

    for (const op of ops) {
        const insert = op.insert;

        if (typeof insert === "string") {
            const attrs = op.attributes ?? {};

            const textLen = insert.replace(/\n/g, "").length;
            if (textLen > 0) {
                const color = (attrs.color ?? defaultColor) as string;
                colors.add(color);
                links.add((attrs.link as string) || "");
                boldValues.add((attrs.bold as boolean) || false);
                strikeValues.add((attrs.strike as boolean) || false);
                italicValues.add((attrs.italic as boolean) || false);
                underlineValues.add((attrs.underline as boolean) || false);
            }

            if (insert.includes("\n")) {
                const newlineCount = countNewlines(insert);
                if (newlineCount > 0) {
                    const list = attrs.list;
                    if (list === "bullet") {
                        hasBulletList = true;
                    }
                }
            }
        }
    }

    const linkValuesArray = Array.from(links);
    const boldValuesArray = Array.from(boldValues);
    const strikeValuesArray = Array.from(strikeValues);
    const colorsArray = Array.from(colors);
    const italicValuesArray = Array.from(italicValues);
    const underlineValuesArray = Array.from(underlineValues);

    const link = linkValuesArray.length > 1 ? "" : linkValuesArray[0];
    const isBold = boldValuesArray.length > 1 ? false : boldValuesArray[0];
    const isStriked = strikeValuesArray.length > 1 ? false : strikeValuesArray[0];
    const isItalic = italicValuesArray.length > 1 ? false : italicValuesArray[0];
    const isUnderline = underlineValuesArray.length > 1 ? false : underlineValuesArray[0];
    const firstColor = colorsArray[0] ?? defaultColor;

    return {
        firstColor,
        secondColor: colorsArray[1] ?? firstColor,
        link,
        hasBulletList,
        isBold,
        isStriked,
        isItalic,
        isUnderline,
    };
}

export function isSameLine(a: Bounds, b: Bounds) {
    return Math.abs(a.top - b.top) <= Math.min(a.height, b.height) * 0.5;
}

function isQuillRangeError(error: unknown): error is DOMException {
    if (!(error instanceof DOMException)) {
        return false;
    }

    return (
        error.name === "IndexSizeError" ||
        error.message.includes("Failed to execute 'setStart' on 'Range'") ||
        error.message.includes("Failed to execute 'setEnd' on 'Range'")
    );
}

function reportQuillBoundsRangeError(error: unknown, meta: Record<string, unknown>) {
    const now = Date.now();
    if (
        lastQuillBoundsRangeErrorReportAt > 0 &&
        now - lastQuillBoundsRangeErrorReportAt < QUILL_BOUNDS_REPORT_INTERVAL_MS
    ) {
        return;
    }

    lastQuillBoundsRangeErrorReportAt = now;

    withScope((scope) => {
        scope.setTag("scope", "quill.getBounds");
        scope.setFingerprint(["quill.getBounds.range-error"]);
        scope.setContext("quillBounds", {
            ...meta,
            reportIntervalMs: QUILL_BOUNDS_REPORT_INTERVAL_MS,
        });
        captureException(error);
    });
}

export function getSafeBounds(quill: Quill, index: number, length = 0): Bounds | null {
    const maxIndex = Math.max(0, quill.getLength() - 1);
    const safeIndex = clamp(index, 0, maxIndex);
    const safeLength = Math.max(0, Math.min(length, maxIndex - safeIndex));
    const readBounds = () => (quill.getBounds(safeIndex, safeLength) as Bounds | null) ?? null;
    const meta = {
        index,
        length,
        safeIndex,
        safeLength,
        quillLength: quill.getLength(),
    };
    let initialError: unknown = null;

    try {
        return readBounds();
    } catch (error) {
        if (!isQuillRangeError(error)) {
            throw error;
        }

        initialError = error;
    }

    try {
        quill.update("silent");
        const bounds = readBounds();

        reportQuillBoundsRangeError(initialError, {
            ...meta,
            recoveredAfterUpdate: true,
        });

        return bounds;
    } catch (error) {
        if (!isQuillRangeError(error)) {
            throw error;
        }

        reportQuillBoundsRangeError(initialError, {
            ...meta,
            recoveredAfterUpdate: false,
            retryErrorName: error.name,
            retryErrorMessage: error.message,
        });
    }

    return null;
}

export function getBoundsNearest(
    quill: Quill,
    i: number,
    min: number,
    max: number,
): { i: number; b: Bounds } | null {
    const len = max - min + 1;
    if (len <= 0) {
        return null;
    }

    const start = clamp(i, min, max);

    for (let step = 0; step < len; step++) {
        const left = start - step;
        if (left >= min) {
            const b = getSafeBounds(quill, left);
            if (b) {
                return { i: left, b };
            }
        }
        const right = start + step;
        if (step !== 0 && right <= max) {
            const b = getSafeBounds(quill, right);
            if (b) {
                return { i: right, b };
            }
        }
    }
    return null;
}

export function getBoundsOrNull(quill: Quill, i: number): Bounds | null {
    return getSafeBounds(quill, i);
}

export function normalizeColors(color: string | string[] | null | undefined) {
    if (typeof color === "string") {
        return {
            firstColor: color,
            secondColor: color,
        };
    }

    if (Array.isArray(color)) {
        if (color.length === 0) {
            return {
                firstColor: DEFAULT_TEXT_COLOR,
                secondColor: DEFAULT_TEXT_COLOR,
            };
        }

        if (color.length === 1) {
            return {
                firstColor: color[0],
                secondColor: color[0],
            };
        }

        return {
            firstColor: color[0],
            secondColor: color[1],
        };
    }

    return {
        firstColor: DEFAULT_TEXT_COLOR,
        secondColor: DEFAULT_TEXT_COLOR,
    };
}
